import { useCallback, useRef } from 'react';
import { useLocale } from '@hooks/useLocale';
import { useCartUI } from '@context/CartContext';
import {
  AtcSource,
  getCartVariantIds,
  getVariantQuantity,
  pruneAtcSources,
  recordAtcSource,
} from '@utils/cart/atcSource';
import type { Cart, ItemBody } from '../../types/cart';
import { HookError } from '../../errorHandlers';

export class BatchRejectedError extends HookError {
  constructor() {
    super('Batch rejected');
    this.name = 'BatchRejectedError';
  }
}

export const addItemsFetcher = async (
  items: ItemBody[],
  locale: string
): Promise<Cart> => {
  if (
    items.some(
      ({ quantity }) =>
        quantity !== undefined && (!Number.isInteger(quantity) || quantity < 1)
    )
  ) {
    throw new HookError(
      'The item quantity has to be a valid integer greater than 0'
    );
  }

  const response = await fetch('/api/cart', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept-Language': locale,
    },
    body: JSON.stringify({ items }),
  });

  const { data, errors } = await response.json();

  if (response.status === 422 && errors?.[0]?.code === 'BATCH_REJECTED') {
    throw new BatchRejectedError();
  }
  if (!response.ok || !data) {
    throw new HookError(errors?.[0]?.message ?? 'Failed to add items to cart');
  }

  return data;
};

const useAddItems = () => {
  const { cart, updateCart } = useCartUI();
  const cartRef = useRef(cart);
  cartRef.current = cart;
  const locale = useLocale();
  if (!locale)
    throw new HookError('Locale must be specified to add items to cart');

  return useCallback(
    async (items: ItemBody[], atcSource?: AtcSource): Promise<Cart> => {
      const previousLineItems = cartRef.current?.cartData?.line_items;
      const data = await addItemsFetcher(items, locale);
      updateCart(data);
      try {
        const lineItems = data?.cartData?.line_items;
        const cartVariantIds = lineItems
          ? getCartVariantIds(lineItems)
          : undefined;
        items.forEach(({ variantId }) => {
          if (
            getVariantQuantity(lineItems, variantId) >
            getVariantQuantity(previousLineItems, variantId)
          ) {
            recordAtcSource(variantId, atcSource, cartVariantIds);
          } else if (cartVariantIds) {
            pruneAtcSources(cartVariantIds);
          }
        });
      } catch (error) {
        console.log(error);
      }
      return data;
    },
    [locale, updateCart]
  );
};

export default useAddItems;
